import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'How does Birmingham\'s PFI contract affect my claim?',
    answer: 'Birmingham delivers highways maintenance through a PFI contract combining approximately £50m of PFI credits plus a ring-fenced revenue contribution of roughly £56m per annum. Legal liability for road condition remains with Birmingham City Council as the highway authority. The Department for Transport produces no spend scorecard for Birmingham because of these separate funding arrangements — that does not remove the council\'s Section 58 obligation to show it reasonably inspected and repaired the specific defect that damaged your vehicle.',
  },
  {
    question: 'Why is there no DfT spend rating for Birmingham?',
    answer: 'The DfT states that a spend rating has not been produced for authorities with separate highways maintenance funding arrangements. Birmingham\'s PFI contract sits outside the standard capital-allocation comparison used for other councils. You can still cite the council\'s own figures — for example, capital spend of just £4.41m against a £50m DfT allocation in 2024/25, with preventative maintenance at 4% of spend — when challenging whether aggregate investment translated into reasonable maintenance on your road.',
  },
  {
    question: 'What does preventative spend falling from 28% to 4% mean for claims?',
    answer: 'Birmingham\'s transparency report shows estimated preventative maintenance share peaked at 28% in 2022/23 and fell to 4% in 2024/25, while reactive share was 49%. The council\'s own strategy states that "timely intervention using preventative maintenance treatments can extend the surface life of the carriageways" — yet pavement preventative treatment delivered just 2km across the entire network in 2024/25. That policy-practice gap can support a Section 58 argument when tied to your specific defect, but it does not guarantee a successful claim on its own.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 2,021km — 77% of Birmingham\'s 2,632km carriageway network. The council\'s last published U-road red-condition figure is 8% (2023); no U-road condition survey was carried out in 2024 due to the PFI contract tender process. From 2025, PAS 2161 surveys cover unclassified roads annually. If your incident was on a U-road, prior reports via the council\'s online form and photos showing defect age matter more than network-wide A-road scorecards.',
  },
  {
    question: 'What about Birmingham\'s new PAS 2161 survey system?',
    answer: 'The council has surveyed with a PAS 2161-accredited supplier since June 2024. Indicative results show A-roads surveyed monthly, B and C roads quarterly, and unclassified roads annually. That increased survey frequency can make it harder for the council to argue it lacked knowledge of deterioration on surveyed sections — but survey snapshots may not reflect the condition at the exact time of your incident, and U-road surveys were paused in 2024 during the PFI tender.',
  },
  {
    question: 'Birmingham filled 4,514 potholes in 2024/25 — can I still claim?',
    answer: 'Yes. Pothole fills do not automatically defeat a claim. The council estimated 4,514 pothole repairs in 2024/25 — 33% of reactive maintenance spend — and projects 6,788 fills in 2025/26. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired within the council\'s own criteria (carriageway defects over 40mm, footway defects over 20mm). Prior reports of the same pothole and evidence of defect age remain central.',
  },
  {
    question: 'How do I report a pothole to Birmingham City Council?',
    answer: 'Report road and pavement problems via the council\'s online map form at birmingham.gov.uk/potholes. The council asks for an accurate location and description (pothole size, position in the road). Reports are assessed for emergency action or routine inspection. Prior reports strengthen a claim by demonstrating notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BirminghamCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Birmingham Pothole Claims | 2km Preventative Work on 2,632km Network | Fixtyer"
        description="Birmingham City Council earns AMBER DfT scorecards, has no spend rating due to its PFI contract, and delivered just 2km of preventative pavement treatment in 2024/25. See what that means for your Section 58 claim."
        canonical="/councils/birmingham"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Birmingham', url: '/councils/birmingham' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Birmingham City Council"
        councilSlug="birmingham"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/birmingham"
        headline="Birmingham Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Birmingham</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <span className="inline-block px-2 py-0.5 text-xs font-bold rounded-full uppercase bg-surface-600 text-surface-200">No rating</span>
              <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-6 max-w-3xl">
            <p className="text-amber-200 text-sm">
              <strong className="text-amber-400">DfT caveat:</strong> The Department for Transport has{' '}
              <strong className="text-white">not produced a spend scorecard</strong> for Birmingham because it has
              separate highways maintenance funding arrangements through its PFI contract.
            </p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            2km of Preventative Work Across 2,632km in 2024/25
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Birmingham City Council maintains one of England&apos;s largest urban road networks —{' '}
            <strong className="text-rose-400">2,632km of carriageway</strong>, 77% unclassified residential roads.
            The DfT rates the city <strong className="text-amber-400">AMBER</strong> on condition and best practice.
            Its own report shows preventative maintenance share collapsed from{' '}
            <strong className="text-rose-400">28% to 4%</strong> between 2022/23 and 2024/25, capital spend fell to{' '}
            <strong className="text-rose-400">£4.41m</strong> against a £50m DfT allocation, and just{' '}
            <strong className="text-rose-400">2km</strong> of pavement preventative treatment was delivered. Section 58
            still turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">4%</div>
            <div className="text-white font-medium mt-2">Preventative maintenance share 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              Down from 28% in 2022/23. Capital spend was £4.41m of a £50m DfT allocation — £45.6m unspent on capital
              works that year — while the council projects 25% preventative share and £26.4m capital spend for 2025/26.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The DfT Scorecards
          </h2>
          <p className="text-surface-600 mb-8">
            Official 2025/26 ratings from the Department for Transport — three scorecards plus overall
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full">
              <caption className="sr-only">Birmingham City Council DfT Road Maintenance Scorecards 2025 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Scorecard</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center text-surface-600 font-medium">No rating</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Wider best practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-4">
            <p className="text-amber-900 text-sm">
              <strong>What AMBER means:</strong> An AMBER overall rating indicates mixed or weaker performance across
              condition, spend and best-practice scorecards relative to green-rated authorities. For claims, the rating
              is contextual evidence — Section 58 still requires the council to prove it reasonably inspected and
              repaired the specific defect that damaged your vehicle.
            </p>
          </div>

          <p className="text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
            . Spend scorecard omitted for Birmingham due to separate PFI funding arrangements (DfT table note **).
          </p>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            2,632km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Birmingham&apos;s 2025/26 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">2,632km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">2,021km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">77% of the network</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">306km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">305km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Birmingham highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">5,127km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Bridges, retaining walls and structures</td>
                  <td className="px-6 py-4 text-center font-medium">830 structures</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Street lighting columns</td>
                  <td className="px-6 py-4 text-center font-medium">98,731</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Highway gullies</td>
                  <td className="px-6 py-4 text-center font-medium">106,866</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Birmingham City Council has one of the largest road networks in the country. It is the most valuable physical asset for which the Council is responsible for."
            source="Birmingham City Council Local Highway Maintenance Funding Report 2025 to 2026"
          />
        </div>
      </section>

      {/* Condition */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What AMBER Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER surveys 2010–2024 on classified roads; PAS 2161 from June 2024 — with survey gaps on U-roads
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveats:</strong> Classified A, B and C roads were surveyed with SCANNER laser
              technology through 2024. From 2025 the council adopted PAS 2161 collection. No U-road condition survey
              was carried out in 2024 due to the PFI contract tender process. The council also notes 2024 skid-resistance
              survey figures were affected by contractor benchmark data issues — principal roads at or below investigatory
              level rose to 41% and non-principal to 56%, but the report flags quality-assurance problems with that survey.
            </p>
          </div>

          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (306km) — SCANNER RCI 2020 to 2024</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">77%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6%</td>
                    <td className="px-4 py-2 text-center font-bold">26%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">68%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Green-rated A-roads fell from 77% to 68% over five years; red condition doubled from 3% to 6%.
            </p>
          </div>

          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (305km) — SCANNER RCI 2020 to 2024</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">80%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6%</td>
                    <td className="px-4 py-2 text-center font-bold">25%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">69%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C green condition fell from 80% to 69%; red condition tripled from 2% to 6%.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (2,021km) — red condition only published</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-surface-500">Amber / Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">12%</td>
                    <td className="px-4 py-2 text-center text-surface-500 italic">Not published separately</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">9%</td>
                    <td className="px-4 py-2 text-center text-surface-500 italic">Not published separately</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={2}>No survey — PFI contract tender process</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 text-sm">
              U-roads were previously surveyed on a rolling two-year cycle via detailed visual inspection. The council
              delivered just 2km of preventative treatment on unclassified roads in 2024/25 — 0.1% of the 2,021km
              U-road network.
            </p>
          </div>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
            <h3 className="font-semibold text-brand-900 mb-4">PAS 2161 indicative results (from June 2024)</h3>
            <p className="text-surface-600 text-sm mb-4">
              Monthly A-road surveys, quarterly B/C surveys, annual U-road surveys under the new standard. Indicative
              A-road results: 88% no deterioration (Level 1), 1% severe deterioration (Level 5). These are forward-looking
              survey improvements — not a substitute for evidence about your specific defect at the time of your incident.
            </p>
            <QuoteCard
              quote="The ability to make the right decision at the right time is key to effective lifecycle planning. Using the data available from the condition surveys will ensure that the asset will provide the required level of service over its expected life span at the most efficient cost."
              source="Birmingham City Council Local Highway Maintenance Funding Report 2025 to 2026"
            />
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            DfT capital allocation vs actual spend — PFI contract means no DfT spend scorecard, but council figures are published
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Birmingham highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£50.3m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">£26.4m</td>
                  <td className="px-4 py-3 text-center">£62.5m</td>
                  <td className="px-4 py-3 text-center text-emerald-400">25%</td>
                  <td className="px-4 py-3 text-center">59%</td>
                </tr>
                <tr className="bg-rose-900/30">
                  <td className="px-4 py-3 font-medium">2024/25</td>
                  <td className="px-4 py-3 text-center">£50.0m</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-400">£4.41m</td>
                  <td className="px-4 py-3 text-center">£52.0m</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-400">4%</td>
                  <td className="px-4 py-3 text-center">49%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">£50.3m</td>
                  <td className="px-4 py-3 text-center">£16.7m</td>
                  <td className="px-4 py-3 text-center">£77.6m</td>
                  <td className="px-4 py-3 text-center">15%</td>
                  <td className="px-4 py-3 text-center">73%</td>
                </tr>
                <tr className="bg-emerald-900/20">
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">£50.0m</td>
                  <td className="px-4 py-3 text-center text-emerald-400">£29.0m</td>
                  <td className="px-4 py-3 text-center">£68.1m</td>
                  <td className="px-4 py-3 text-center text-emerald-400">28%</td>
                  <td className="px-4 py-3 text-center">65%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">£50.3m</td>
                  <td className="px-4 py-3 text-center">£27.0m</td>
                  <td className="px-4 py-3 text-center">£56.9m</td>
                  <td className="px-4 py-3 text-center">26%</td>
                  <td className="px-4 py-3 text-center">55%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">£50.3m</td>
                  <td className="px-4 py-3 text-center">£12.3m</td>
                  <td className="px-4 py-3 text-center">£43.0m</td>
                  <td className="px-4 py-3 text-center">12%</td>
                  <td className="px-4 py-3 text-center">41%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-brand-900/30 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-white mb-3">PFI funding context</h3>
              <p className="text-brand-200 text-sm">
                Highways maintenance is delivered through a PFI contract combining approximately £50m of PFI credits
                plus roughly £56m per annum of ring-fenced revenue contribution. The council and DfT are in discussions
                on the remaining PFI term; interim services continue via the current interim services contract.
              </p>
            </div>
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">2024/25 capital underspend</h3>
              <p className="text-brand-200 text-sm">
                Birmingham spent £4.41m of its £50m DfT capital allocation in 2024/25 — leaving £45.6m (91%) unspent
                on capital works that year. Capital spend fell 85% from the £29.0m peak in 2022/23. Aggregate figures
                do not prove your specific defect was maintained — but they contextualise the council&apos;s maintenance story.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Birmingham's Highways Maintenance and Management services are currently delivered through a PFI contract. The contract combines approximately £50m PFI funding from government in the form of PFI credits, in addition to the council's ring-fenced revenue budget contribution - approximately £56m per annum."
            source="Birmingham City Council Local Highway Maintenance Funding Report 2025 to 2026"
          />
        </div>
      </section>

      {/* Prevention collapse */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Preventative Treatment Delivered
          </h2>
          <p className="text-surface-600 mb-8">
            Pavement treatment kilometres from the council&apos;s transparency report — policy vs delivery
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Birmingham preventative pavement treatment kilometres 2020-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-right font-semibold text-brand-900">A roads</th>
                  <th className="px-4 py-3 text-right font-semibold text-brand-900">B/C roads</th>
                  <th className="px-4 py-3 text-right font-semibold text-brand-900">U roads</th>
                  <th className="px-4 py-3 text-right font-semibold text-brand-900">Footways</th>
                  <th className="px-4 py-3 text-right font-semibold text-brand-900 bg-surface-200">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-right">12km</td>
                  <td className="px-4 py-3 text-right">5km</td>
                  <td className="px-4 py-3 text-right">24km</td>
                  <td className="px-4 py-3 text-right">28km</td>
                  <td className="px-4 py-3 text-right font-medium bg-surface-50">69km</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-right">11km</td>
                  <td className="px-4 py-3 text-right">13km</td>
                  <td className="px-4 py-3 text-right">51km</td>
                  <td className="px-4 py-3 text-right">100km</td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-600 bg-emerald-100">175km</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-right text-amber-600">1km</td>
                  <td className="px-4 py-3 text-right text-amber-600">3km</td>
                  <td className="px-4 py-3 text-right text-amber-600">15km</td>
                  <td className="px-4 py-3 text-right">0km</td>
                  <td className="px-4 py-3 text-right font-medium text-amber-600 bg-amber-50">19km</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-4 py-3 font-mono font-bold">2024/25</td>
                  <td className="px-4 py-3 text-right text-rose-600">0km</td>
                  <td className="px-4 py-3 text-right text-rose-600">0km</td>
                  <td className="px-4 py-3 text-right text-rose-600">2km</td>
                  <td className="px-4 py-3 text-right">0km</td>
                  <td className="px-4 py-3 text-right font-bold text-rose-600 bg-rose-100">2km</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Timely intervention using preventative maintenance treatments can extend the surface life of the carriageways and delay the need for major intervention. Adopting an approach that focuses on preventative maintenance will extend the service lives of Birmingham's carriageways, optimise the whole life costs of maintaining the assets..."
              source="Birmingham City Council Local Highway Maintenance Funding Report 2025 to 2026"
            />
            <QuoteCard
              quote="Asset deterioration is an inevitable consequence of aging, the existing construction materials, traffic volumes and characteristics, weather and in some cases, pollution."
              source="Birmingham City Council Local Highway Maintenance Funding Report 2025 to 2026"
            />
          </div>

          <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths</h3>
            <p className="text-surface-600 text-sm">
              At 2024/25 delivery rates (2km per year), treating the full 2,632km carriageway network would take over
              1,300 years. The council projects 2025/26 preventative pavement treatment of 4 miles on A-roads, 3 miles
              each on B and C roads, 10 miles on unclassified roads and 7 miles of footways — provisional and subject
              to change.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Pothole Repairs and Reactive Workload
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — defects the council defines as carriageway issues over 40mm
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Birmingham estimated potholes filled 2020-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of reactive spend</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">9,710</td>
                  <td className="px-6 py-4 text-center">69%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">6,616</td>
                  <td className="px-6 py-4 text-center">44%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">9,332</td>
                  <td className="px-6 py-4 text-center">44%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,514</td>
                  <td className="px-6 py-4 text-center">33%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">6,788</td>
                  <td className="px-6 py-4 text-center text-surface-500">Council estimate</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Inspection frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Roads, pavements and cycleways inspected monthly to annually by road class</li>
                <li>• PAS 2161: A-roads monthly, B/C quarterly, U-roads annually (from 2025)</li>
                <li>• Structures: general or principal inspections every two years</li>
                <li>• Defects between inspections can be reported via the council&apos;s online tool</li>
              </ul>
            </div>
            <div className="bg-brand-950 text-white rounded-xl p-6">
              <h3 className="font-semibold mb-3">Section 41 vs Section 58</h3>
              <p className="text-brand-200 text-sm">
                Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Birmingham must
                maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>,
                it must show a reasonable system for inspecting and repairing the specific defect — not just publish
                an AMBER DfT scorecard or cite PFI contractor arrangements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER-Rated City Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Birmingham&apos;s PFI complexity and projected 2025/26 recovery do not remove your right to claim
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AMBER overall — not red-rated
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Projected 2025/26 capital spend of £26.4m with 25% preventative share
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> PAS 2161 surveys from June 2024 with increased frequency on classified roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> PFI contract delivers ~£106m combined annual highways funding
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road red condition fell from 12% to 8% on last published DVI survey (2020–2023)
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect PFI complexity and aggregate funding figures in the council&apos;s Section 58 defence.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Preventative share fell from 28% to 4%; just 2km pavement treatment in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> £45.6m of £50m DfT capital allocation unspent in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A and B/C green condition fell to 68–69%; red condition doubled or tripled
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No U-road condition survey in 2024; 77% of network on U-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Policy-practice gap on preventative maintenance the council describes as &quot;key&quot;
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> DfT AMBER condition and best-practice scorecards
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a PFI-funded authority with AMBER scorecards, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole via birmingham.gov.uk/potholes — proof of actual notice</li>
              <li>• Photos showing defect size (over 40mm on carriageways), depth and visible age</li>
              <li>• Road class — on a U-road, the 2024 survey gap and 2km preventative delivery are structural arguments</li>
              <li>• Whether PAS 2161 survey data covered your section before your incident</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Birmingham&apos;s
              own transparency data — including the DfT spend-scorecard omission — where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Birmingham City Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. The council assesses reports for emergency
            action or routine inspection against its repair criteria. Do this before claiming — and tell us when you
            reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.birmingham.gov.uk/potholes"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a road or pavement problem — birmingham.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Birmingham?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A PFI-funded network demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No PFI spend-scorecard gap argument</li>
                <li>• No 28%→4% prevention collapse cited</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ AMBER DfT scorecards and spend omission documented</li>
                <li>✅ 2km preventative delivery and 4% share cited</li>
                <li>✅ £45.6m capital underspend evidence</li>
                <li>✅ PFI context and U-road survey gap addressed</li>
                <li>✅ Section 58 rebuttal tailored to Birmingham</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=birmingham"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Birmingham Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.birmingham.gov.uk/download/downloads/id/30421/birmingham_local_highway_maintenance_funding_2025_to_2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Birmingham City Council Local Highway Maintenance Funding Report 2025 to 2026
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
